import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axios from "axios";
import strings from "../global/strings";
import type { Bill, CreditNote } from "./bill.queries";
import type { Customer } from "./customer.queries";

export const activityQueryKey = ["activity"] as const;

// Mirrors billing_system_BE/controllers/activityLog.controller.js's
// getActivityByCompany includes. bill/creditNote/customer_info are each
// included with no `attributes` restriction (commented out on the BE side),
// so they're the full model row — safe to reuse Bill/CreditNote/Customer
// directly rather than inventing narrower duplicate types.
export interface ActivityUser {
	user_id: string;
	first_name: string;
	last_name: string;
	user_email?: string;
	user_image?: string | null;
	user_mobile?: string;
}

export interface Activity {
	activity_log_id: string;
	company_info_id: string;
	company_user_id: string;
	model_id?: string | null;
	request_method: string;
	request_model: string;
	activity_type: string;
	activity_note: string;
	createdAt: string;
	updatedAt?: string;
	// default belongsTo alias (no `as`) — confirmed via activity-item.component.jsx's
	// `activity.company_user.user` runtime access
	company_user: { user: ActivityUser };
	bill?: Partial<Bill> | null;
	creditNote?: Partial<CreditNote> | null;
	customer_info?: Partial<Customer> | null;
}

interface ActivityLogPage {
	rows: Activity[];
	count: number;
}

async function fetchBillAuditLog(billId: string): Promise<ActivityLogPage> {
	const res = await axios.get(`${strings.url}activity/id`, {
		params: { model_id: billId },
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return { rows: res.data.result.rows, count: res.data.result.count };
}

// Unpaginated — bill-audit-log.component.tsx prints the full history for one bill.
export function useBillAuditLog(billId: string) {
	return useQuery({
		queryKey: [...activityQueryKey, "bill", billId],
		queryFn: () => fetchBillAuditLog(billId),
		enabled: !!billId,
	});
}

async function fetchActivityLog(
	startMonth: string,
	limit: number,
	page: number,
): Promise<ActivityLogPage> {
	const res = await axios.get(`${strings.url}activity/company`, {
		params: { startMonth, limit, page },
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return { rows: res.data.result.rows, count: res.data.result.count };
}

// Infinite-scroll activity list, keyed by the applied month filter — mirrors
// useCustomers in customer.queries.ts. Changing startMonth resets pagination
// since it's part of the query key.
export function useActivityLog(startMonth: string, limit: number) {
	return useInfiniteQuery({
		queryKey: [...activityQueryKey, startMonth, limit],
		queryFn: ({ pageParam }) => fetchActivityLog(startMonth, limit, pageParam),
		initialPageParam: 1,
		getNextPageParam: (lastPage, allPages) => {
			const fetchedCount = allPages.reduce((sum, p) => sum + p.rows.length, 0);
			return fetchedCount < lastPage.count ? allPages.length + 1 : undefined;
		},
	});
}
